/**
 * brandApi — brand-specific API calls.
 * Uses apiClient from @discovr/common.
 */
import { apiClient } from '@discovr/common';
import type { PaginatedResponse, DataResponse } from '@discovr/common';
import type { ResolvedAudio } from '@discovr/types';
import { resolveOfficialAudio as resolveOfficialAudioShared } from '@discovr/features';

export type SearchIntentFilters = {
  query: string;
  chips: string[];
  min_followers?: number;
  max_followers?: number;
  category?: string;
  language?: string;
};

export type SearchIntentResponse = {
  query: string;
  chips: string[];
  intent_summary?: string;
};

// ── Brand profile ─────────────────────────────────────────────────────────────

/**
 * Brand profile shape from GET /api/v1/brands/me.
 * Field names verified against live BE (2026-05-15).
 */
export interface BrandProfile {
  id: string;
  owner_id: string;
  name: string;
  /** BE field name is brand_logo (not logo_url). */
  brand_logo?: string;
  slug?: string;
  website?: string;
  socials?: { instagram?: string; youtube?: string };
  created_at?: string;
  updated_at?: string;
}

// ── Dashboard ─────────────────────────────────────────────────────────────────

export interface DashboardKPIs {
  active_campaigns: number;
  total_creators: number;
  live_content: number;
  total_reach: number;
}

export interface DashboardResponse {
  kpis: DashboardKPIs;
}

/**
 * Row returned by GET /api/v1/campaigns?expand=progress.
 * Re-uses the canonical CampaignListItem shape from @discovr/api and
 * narrows `progress` to non-optional since this hook always opts in.
 */
import type { Campaign } from '@discovr/types';
import type { CampaignListItem } from '@discovr/api';
export type CampaignWithProgress = CampaignListItem &
  Required<Pick<CampaignListItem, 'progress'>>;

// ── Activity ──────────────────────────────────────────────────────────────────

// Resolved metadata for a campaign's official Instagram audio (setup preview) —
// re-exported so existing call sites keep importing it from here.
export type { ResolvedAudio };

export interface ActivityRow {
  id: string;
  campaign_id: string;
  entry_id?: string;
  actor_type: string;
  actor_name: string;
  action: string;
  label: string;
  sub_text?: string;
  status_tag: string;
  created_at: string;
}

// ── API object ────────────────────────────────────────────────────────────────

export const brandApi = {
  // ── Profile ──────────────────────────────────────────────────────────────

  getProfile: (): Promise<BrandProfile> =>
    apiClient
      .get<DataResponse<BrandProfile>>('/brands/me')
      .then((r) => r.data.data),

  // ── Home dashboard ────────────────────────────────────────────────────────

  /** GET /api/v1/kpis — brand-rollup KPI cards (active_campaigns, total_creators, live_content, total_reach). */
  getDashboard: (): Promise<DashboardResponse> =>
    apiClient
      .get<DataResponse<DashboardResponse>>('/kpis')
      .then((r) => r.data.data),

  /**
   * GET /api/v1/campaigns?expand=progress — campaign progress widget rows.
   * Stripe-style optional field expansion: the unified campaigns list
   * returns the canonical CampaignListItem shape plus a `progress` object
   * per row (spend_to_date, budget_used_pct, creators-by-status counts)
   * when expand=progress is passed. Default server sort is created_at
   * descending so the widget shows the most-recent campaigns first.
   */
  getDashboardCampaigns: (limit = 5): Promise<PaginatedResponse<CampaignWithProgress>> =>
    apiClient
      .get<PaginatedResponse<CampaignWithProgress>>('/campaigns', {
        params: { limit, expand: 'progress' },
      })
      .then((r) => r.data),

  /** GET /api/v1/activity — brand-scoped activity feed (top 10, server-side fixed). */
  getBrandActivity: (): Promise<PaginatedResponse<ActivityRow>> =>
    apiClient
      .get<PaginatedResponse<ActivityRow>>('/activity')
      .then((r) => r.data),

  // ── Campaign mutations ────────────────────────────────────────────────────

  /** Create a campaign with JSON body. Returns the created campaign. */
  createCampaign: (data: Record<string, unknown>): Promise<Campaign> =>
    apiClient
      .post<DataResponse<Campaign>>('/campaigns', data)
      .then((r) => r.data.data),

  // ── Official Audio Compliance ─────────────────────────────────────────────

  /**
   * POST /api/v1/instagram/audio/resolve — preview an IG audio URL as
   * track/artist/cover for the "Campaign audio" card. Stateless (no campaign
   * needed), so the create wizard can call it before the campaign exists.
   * `resolved` is false when IG's page couldn't be read — the audio_id is still
   * verified and returned.
   */
  resolveOfficialAudio: (audioUrl: string): Promise<ResolvedAudio> => resolveOfficialAudioShared(audioUrl),

};
